import React, { useRef, forwardRef } from "react";
import PropTypes from "prop-types";

import Icon from "@javascript/reactComponents/lds/Icon";
import { isString } from "lodash";

function HelperText({
  helperTextStylesOverride = {},
  showHelperTextIcon = true,
  value = "",
  variant = "none",
}) {
  const iconStyles = {
    visibility: showHelperTextIcon ? "visible" : "hidden",
  };

  return (
    <span
      className={"input-helperText-" + variant}
      style={helperTextStylesOverride}
    >
      <span
        className={"icon-" + variant + "-filled"}
        style={iconStyles}
        role="img"
      />
      <span>{value}</span>
    </span>
  );
}

/**
 * The `autoComplete` prop supports all standard tokens defined by the HTML spec:
 * https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
 * Common values permitted:
 * - "on" / "off" (Enable/disable autocomplete)
 * - "name", "email"", "password" (Basic user info)
 * Other fields we can consider adding in the future
 * - Address fields: "street-address", "postal-code", "address-level1"
 * - Payment fields: "cc-number", "cc-exp", "cc-csc"
 * - Contact fields: "tel", "url"
 */


const Input = ({
  autoComplete = "on",
  className = "",
  disabled = false,
  focused = false,

  forceShowHelperText = false,
  forceHideHelperText = false,
  height,

  helperText = "",
  helperTextStylesOverride = {},
  helperTextVariant = "none",
  id = null,

  label,
  labelPosition = "inside",
  leadingIcon,

  maxLength,
  maxWidth,
  minWidth,
  multiLine = false,
  name = "",
  onValueChange,
  onFocus,
  onSubmit,
  placeholder = " ",
  readOnly = false,
  role = null,

  showClearIcon = true,
  showHelperTextIcon = true,

  showLabel = true,
  size,
  trailingIcon,
  trailingIconOnClick = () => {},
  type = "text",

  value = "",
  variant = "normal",
  width = 219,
}, containerRef) => {
  const inputRef = useRef();

  const clearInput = (event) => {
    event.preventDefault();
    event.stopPropagation();

    inputRef.current?.focus();
    onValueChange("");
  };

  const jsxInput = !isString(value) && React.isValidElement(value);
  const regularInput = !jsxInput && !multiLine;
  const multiLineInput = !jsxInput && multiLine;

  const parentClass = "input-container-" + size;

  const innerContainerClasses = [
    `input-inner-container-${size}`,
    `input-inner-container-${variant}`,
  ];

  const inputClasses = [`lds-input-${size}`, `input-${variant}`];
  const showLabelInside =
    showLabel &&
    labelPosition === "inside" &&
    (multiLineInput || size === "large");

  if (className) {
    inputClasses.push(className);
  }

  // Inner container
  if (multiLine) {
    innerContainerClasses.push("input-inner-container-multiLine-" + size);
    inputClasses.push("input-multiLine-" + size);
  }
  if (leadingIcon) {
    innerContainerClasses.push("has-leading-icon");
  }
  if (disabled) {
    inputClasses.push("input-disable");
    innerContainerClasses.push("input-inner-container-disable");
  }
  if (focused) {
    innerContainerClasses.push("focused");
  }

  // HTML Input
  if (showLabelInside) {
    inputClasses.push("input-has-label");
  }

  const commonInputProps = {
    id,
    maxLength,
    onChange: (event) => onValueChange(event.target.value),
    onKeyUp: (event) => {
      if (onSubmit && event.key === "Enter") {
        onSubmit();
      }
    },
    placeholder,
    value,
  };

  if (name) {
    commonInputProps.name = name;
  }

  if (role) {
    commonInputProps.role = role;
  }

  if (onFocus) {
    commonInputProps.onFocus = (event) => onFocus(event.target.value);
  }

  const renderInput = () => {
    if (regularInput) {
      commonInputProps.autoComplete = autoComplete;
      return (
        <input
          className={inputClasses.join(" ")}
          {...commonInputProps}
          readOnly={readOnly || disabled}
          ref={inputRef}
          type={type}
        />
      );
    }

    if (multiLineInput) {
      return (
        <textarea
          className={`${inputClasses.join(" ")}`}
          {...commonInputProps}
          readOnly={readOnly || disabled}
          style={{ height }}
        />
      );
    }

    if (jsxInput) return value;

    return null;
  };

  const showOuterLabelTop = showLabel && labelPosition === "top";
  const outerLabelClassNames = ["lds-input-outer-label-" + size];

  if (showOuterLabelTop) {
    outerLabelClassNames.push("lds-input-outer-label-top");
  }

  const outerLabel = (
    <span className={outerLabelClassNames.join(" ")}>{label}</span>
  );

  return (
    <div className={parentClass} style={{ width, minWidth, maxWidth }} ref={containerRef}>
      {showOuterLabelTop && outerLabel}

      <div className={innerContainerClasses.join(" ")}>
        {leadingIcon && <Icon name={leadingIcon} className="icon-leading" />}

        {renderInput()}

        {showLabelInside && <label>{label || placeholder}</label>}
        {showClearIcon && !multiLineInput && !readOnly && (
          <span
            className="icon-clear-filled clear-input-trigger"
            onClick={clearInput}
          />
        )}

        {trailingIcon && (
          <Icon
            name={trailingIcon}
            className="icon-trailing"
            onClick={trailingIconOnClick}
          />
        )}
      </div>

      {!forceHideHelperText && (size !== "small" || forceShowHelperText) && (
        <HelperText
          value={helperText}
          variant={helperTextVariant}
          showHelperTextIcon={showHelperTextIcon}
          helperTextStylesOverride={helperTextStylesOverride}
        />
      )}
    </div>
  );
};

export default forwardRef(Input);

Input.propTypes = {
  autoComplete: PropTypes.oneOf([
    "on",
    "off",
    "name",
    "email",
    "password",
    "new-password",
  ]),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  focused: PropTypes.bool,

  forceShowHelperText: PropTypes.bool,
  forceHideHelperText: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  helperText: PropTypes.string,

  helperTextStylesOverride: PropTypes.object,
  helperTextVariant: PropTypes.oneOf([
    "critical",
    "hidden",
    "info",
    "none",
    "success",
    "warning",
  ]),
  id: PropTypes.string,

  label: PropTypes.string,
  labelPosition: PropTypes.oneOf(["inside", "top"]),
  leadingIcon: PropTypes.string,

  maxLength: PropTypes.number,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  multiLine: PropTypes.bool,
  name: PropTypes.string,
  onValueChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onSubmit: PropTypes.func,

  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  role: PropTypes.string,
  showClearIcon: PropTypes.bool,

  showHelperTextIcon: PropTypes.bool,
  showLabel: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium", "large"]).isRequired,

  trailingIcon: PropTypes.string,
  trailingIconOnClick: PropTypes.func,
  type: PropTypes.oneOf([
    "text",
    "password",
    "email",
    "number",
    "tel",
    "url",
    "search",
    "date",
    "time",
    "datetime-local",
    "month",
    "week",
    "color",
  ]),
  value: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string,
  ]),

  variant: PropTypes.oneOf(["critical", "normal"]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

